import styled from "@emotion/styled";
import { LabelledValue } from "@kaltura/mediaspace-shared-types";
import {
    CheckboxField,
    RadioGroup,
    SelectField,
    SwitchField,
    TextField,
} from "@kaltura/mediaspace-shared-ui/controlled-form";

interface CreateTownhallFormFieldBase {
    id: string;
    label: string;
    description?: string;
    required?: boolean;
    order?: number;
}

interface CreateTownhallSelectFormField extends CreateTownhallFormFieldBase {
    type: "select";
    options: LabelledValue[];
    defaultValue?: string;
}

interface CreateTownhallRadioFormField extends CreateTownhallFormFieldBase {
    type: "radio";
    options: LabelledValue[];
    defaultValue?: string;
}

interface CreateTownhallTextFormField extends CreateTownhallFormFieldBase {
    type: "text";
    defaultValue?: string;
    placeholder?: string;
}

interface CreateTownhallSwitchFormField extends Omit<CreateTownhallFormFieldBase, "required"> {
    type: "switch";
    defaultValue?: boolean;
}

interface CreateTownhallCheckboxFormField extends CreateTownhallFormFieldBase {
    type: "checkbox";
    defaultValue?: boolean;
}

export type CreateTownhallFormField =
    | CreateTownhallSelectFormField
    | CreateTownhallRadioFormField
    | CreateTownhallTextFormField
    | CreateTownhallSwitchFormField
    | CreateTownhallCheckboxFormField;

export const getCreateTownhallFieldDefaultValue = (field: CreateTownhallFormField): string | boolean => {
    switch (field.type) {
        case "switch":
        case "checkbox":
            return field.defaultValue ?? false;
        case "select":
        case "radio":
        case "text":
            return field.defaultValue ?? "";
    }
};

interface CreateTownhallFormFieldsProps {
    fields: CreateTownhallFormField[];
    classNamePrefix: string;
}

const StyledFields = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
}));

/**
 * Renders module-injected Create Town Hall form fields by type.
 */
export const CreateTownhallFormFields = ({ fields, classNamePrefix }: CreateTownhallFormFieldsProps) => {
    return (
        <StyledFields className={`${classNamePrefix}-fields`}>
            {fields.map((field) => {
                const className = `${classNamePrefix}-field-${field.id}`;

                switch (field.type) {
                    case "select":
                        return (
                            <SelectField
                                key={field.id}
                                id={field.id}
                                label={field.label}
                                description={field.description}
                                options={field.options}
                                required={field.required}
                                descriptionTooltip={false}
                                fontWeightBold={true}
                                fullWidth={true}
                                inline={true}
                                className={className}
                            />
                        );
                    case "radio":
                        return (
                            <RadioGroup
                                key={field.id}
                                id={field.id}
                                label={field.label}
                                description={field.description}
                                options={field.options}
                                required={field.required}
                                inline={true}
                            />
                        );
                    case "text":
                        return (
                            <TextField
                                key={field.id}
                                id={field.id}
                                label={field.label}
                                description={field.description}
                                placeholder={field.placeholder}
                                required={field.required}
                                fontWeightBold={true}
                                inline={true}
                                className={className}
                            />
                        );
                    case "switch":
                        return (
                            <SwitchField
                                key={field.id}
                                id={field.id}
                                label={field.label}
                                description={field.description}
                                fontWeightBold={true}
                                inline={true}
                                className={className}
                            />
                        );
                    case "checkbox":
                        return (
                            <CheckboxField
                                key={field.id}
                                id={field.id}
                                label={field.label}
                                description={field.description}
                                required={field.required}
                                fontWeightBold={true}
                                inline={true}
                                className={className}
                            />
                        );
                    default:
                        return null;
                }
            })}
        </StyledFields>
    );
};
